// ============================================================
// HOUSE OF CARDS — QTEngine v1
// One reusable component for every quick-time event in the game.
//   type="press" — hit the shown key before the bar drains (dodge the beam)
//   type="mash"  — hammer SPACE to fill the bar before time runs out
// Used by the prologue now; jumpscare-QTEs reuse this verbatim later.
// ============================================================
import React, { useEffect, useRef, useState, useCallback } from "react";
import { RM, emberGlow } from "./theme";

export default function QTE({
  type = "press",          // "press" | "mash"
  keyName = "E",           // key to hit (press mode)
  windowMs = 3000,         // time allowed
  mashTarget = 14,         // presses needed (mash mode)
  prompt = "",             // flavor line shown above the key
  onResult,                // (success: boolean) => void
}) {
  const [timeLeft, setTimeLeft] = useState(windowMs);
  const [mashCount, setMashCount] = useState(0);
  const [done, setDone] = useState(false);
  const doneRef = useRef(false);
  const startRef = useRef(Date.now());

  const finish = useCallback(
    (success) => {
      if (doneRef.current) return;
      doneRef.current = true;
      setDone(true);
      setTimeout(() => onResult && onResult(success), success ? 350 : 650);
    },
    [onResult]
  );

  // Countdown
  useEffect(() => {
    const tick = setInterval(() => {
      const left = windowMs - (Date.now() - startRef.current);
      setTimeLeft(left);
      if (left <= 0) finish(false);
    }, 50);
    return () => clearInterval(tick);
  }, [windowMs, finish]);

  // Input
  useEffect(() => {
    const onKey = (e) => {
      if (doneRef.current) return;
      if (type === "press") {
        if (e.key.toUpperCase() === keyName.toUpperCase()) finish(true);
        else finish(false); // wrong key = the beam wins
      } else {
        if (e.code === "Space" || e.key === " ") {
          e.preventDefault();
          setMashCount((c) => {
            const n = c + 1;
            if (n >= mashTarget) finish(true);
            return n;
          });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [type, keyName, mashTarget, finish]);

  const pct =
    type === "press"
      ? Math.max(0, timeLeft / windowMs) * 100
      : Math.min(100, (mashCount / mashTarget) * 100);

  return (
    <div style={S.wrap} className={done ? "" : "qte-pulse"}>
      {prompt && <div style={S.prompt}>{prompt}</div>}

      <div
        style={{
          ...S.keyCap,
          boxShadow: emberGlow(timeLeft < windowMs * 0.35 ? RM.fleshGlow : RM.ember),
        }}
        // mobile/tap support: tapping the keycap counts
        onPointerDown={() => {
          if (doneRef.current) return;
          if (type === "press") finish(true);
          else
            setMashCount((c) => {
              const n = c + 1;
              if (n >= mashTarget) finish(true);
              return n;
            });
        }}
      >
        {type === "press" ? keyName.toUpperCase() : "SPACE"}
      </div>

      <div style={S.subLabel}>
        {type === "press" ? "PRESS — NOW" : "MASH TO ESCAPE"}
      </div>

      <div style={S.barOuter}>
        <div
          style={{
            ...S.barInner,
            width: `${pct}%`,
            background:
              type === "mash"
                ? RM.moldBright
                : pct < 35
                ? RM.fleshGlow
                : RM.ember,
          }}
        />
      </div>
    </div>
  );
}

const S = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    padding: "26px 34px",
    background: "rgba(7,5,4,0.82)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 10,
  },
  prompt: {
    color: RM.bone,
    fontFamily: RM.font,
    fontSize: 18,
    fontStyle: "italic",
    textAlign: "center",
    maxWidth: 420,
  },
  keyCap: {
    minWidth: 86,
    padding: "18px 26px",
    textAlign: "center",
    fontFamily: RM.fontD,
    fontSize: 34,
    fontWeight: 700,
    color: RM.emberBright,
    background: RM.char2,
    border: `2px solid ${RM.ember}`,
    borderRadius: 12,
    cursor: "pointer",
    userSelect: "none",
  },
  subLabel: {
    color: RM.boneDim,
    fontFamily: RM.fontD,
    letterSpacing: 3,
    fontSize: 13,
  },
  barOuter: {
    width: 300,
    height: 10,
    background: RM.char1,
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 6,
    overflow: "hidden",
  },
  barInner: {
    height: "100%",
    transition: "width 60ms linear",
  },
};
